import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ROOT_URL } from '../../api/ApiConfig';
import { clearSessionScopedState } from '../../utils/clearSessionScopedState';
import { withoutToken } from '../../utils/authSession';
import { resetAuthExpiryGate } from '../../api/authHttp';

export const loginUserAPI = createAsyncThunk(
  'master-user/login',
  async (requestHeaders, { rejectWithValue }) => {
    try {
      const response = await fetch(`${ROOT_URL}/master-user/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestHeaders),
      });
      const result = await response.json();
      if (result?.user?._id) resetAuthExpiryGate();
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error.message);
    }
  },
);

export const verifyTwoFactorLoginAPI = createAsyncThunk(
  'master-user/verify-two-factor',
  async (requestHeaders, { rejectWithValue }) => {
    try {
      const response = await fetch(`${ROOT_URL}/master-user/login/verify-two-factor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestHeaders),
      });
      const result = await response.json();
      if (result?.success && result?.user?._id) resetAuthExpiryGate();
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error.message);
    }
  },
);

const initialState = {
  isLoading: false,
  isSuccess: false,
  isError: false,
  isCompleted: false,
  response: [],
  error: null,
  requiresTwoFactor: false,
  twoFactorToken: null,
  verifyTwoFactorLoading: false,
};

const loginUser = createSlice({
  name: 'loginUser',
  initialState,
  reducers: {
    resetUser: (state) => ({
      ...state,
      isLoading: false,
      isSuccess: false,
      isError: false,
      isCompleted: false,
      response: [],
      error: null,
      requiresTwoFactor: false,
      twoFactorToken: null,
      verifyTwoFactorLoading: false,
    }),
    clearTwoFactorLogin: (state) => {
      state.requiresTwoFactor = false;
      state.twoFactorToken = null;
      state.verifyTwoFactorLoading = false;
      state.error = null;
    },
    updateUser: (state, action) => {
      if (state.response && state.response.user) {
        state.response.user = {
          ...state.response.user,
          ...action.payload.user
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUserAPI.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUserAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;

        if (action.payload?.requiresTwoFactor) {
          state.requiresTwoFactor = true;
          state.twoFactorToken = action.payload.twoFactorToken ?? null;
          state.response = [];
          return;
        }

        state.requiresTwoFactor = false;
        state.twoFactorToken = null;
        state.response = withoutToken(action.payload);

        if (action.payload?.user) {
          // Drop previous tab's group/tenant session so a new business login cannot reuse it.
          clearSessionScopedState();
        }
      })
      .addCase(loginUserAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(verifyTwoFactorLoginAPI.pending, (state) => {
        state.verifyTwoFactorLoading = true;
        state.error = null;
      })
      .addCase(verifyTwoFactorLoginAPI.fulfilled, (state, action) => {
        state.verifyTwoFactorLoading = false;

        if (action.payload?.success && action.payload?.user) {
          state.requiresTwoFactor = false;
          state.twoFactorToken = null;
          state.isSuccess = true;
          state.isError = false;
          state.response = withoutToken(action.payload);
          clearSessionScopedState();
        }
      })
      .addCase(verifyTwoFactorLoginAPI.rejected, (state, action) => {
        state.verifyTwoFactorLoading = false;
        state.isError = true;
        state.error = action.payload;
      });
  },
});

export const { resetUser, updateUser, clearTwoFactorLogin } = loginUser.actions;
export default loginUser;
